import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import type { ThreadV4ViewSpec } from "../../api/threadV4Views";
import { ThreadLogo } from "../../components/ThreadLogo";
import { openThreadFeedbackReporter } from "../../components/feedback/FeedbackReporter";
import { Icons } from "../thread-v4/icons";
import type { V5View } from "./ThreadV5Workbench";
import "./thread-v5-start-menu.css";

interface MenuDestination {
  view: V5View;
  label: string;
  icon: ReactNode;
  authOnly?: boolean;
}

interface MenuGroup {
  id: string;
  label: string;
  items: MenuDestination[];
}

const MENU_GROUPS: MenuGroup[] = [
  {
    id: "runs",
    label: "Runs",
    items: [{ view: "workbench", label: "Workbench", icon: <Icons.GridIcon size={15} /> }],
  },
  {
    id: "build",
    label: "Build",
    items: [
      { view: "parametric-geometry", label: "Loom", icon: <Icons.Cube size={15} />, authOnly: true },
    ],
  },
  {
    id: "model",
    label: "Model",
    items: [
      { view: "data", label: "Data table", icon: <Icons.Table size={15} /> },
      { view: "tailor", label: "Aerodynamic Database", icon: <Icons.GridIcon size={15} /> },
      { view: "filament-model", label: "Aero Design", icon: <Icons.Flowfield size={15} />, authOnly: true },
      { view: "training", label: "Adaptive sampling", icon: <Icons.Sparkle size={15} />, authOnly: true },
      { view: "flow360-import", label: "Flow360 import", icon: <Icons.Flowfield size={15} />, authOnly: true },
      { view: "artifacts", label: "Artifacts", icon: <Icons.Asset size={15} /> },
    ],
  },
  {
    id: "trust",
    label: "Trust",
    items: [
      { view: "interpolant-audit", label: "Audit", icon: <Icons.Info size={15} /> },
      { view: "airshow-coverage", label: "Airshow Coverage", icon: <Icons.Info size={15} />, authOnly: true },
      { view: "lineage", label: "Lineage", icon: <Icons.Dag size={15} /> },
      { view: "selvedge", label: "Selvedge", icon: <Icons.Search size={15} /> },
    ],
  },
  {
    id: "project",
    label: "Project",
    items: [
      { view: "projects", label: "Projects", icon: <Icons.Project size={15} />, authOnly: true },
      { view: "branching", label: "Branching", icon: <Icons.Branch size={15} /> },
      { view: "members", label: "Members", icon: <Icons.Users size={15} />, authOnly: true },
    ],
  },
  {
    id: "boards",
    label: "Boards",
    items: [
      { view: "home", label: "Home", icon: <Icons.Project size={15} /> },
      { view: "dashboard-edit", label: "New board", icon: <Icons.Plus size={15} />, authOnly: true },
    ],
  },
];

const VIEW_ALIAS: Partial<Record<V5View, V5View>> = {
  aircraft: "parametric-geometry",
  composite: "tailor",
  "control-surfaces": "parametric-geometry",
  highlift: "parametric-geometry",
  provenance: "lineage",
  "wing-section": "parametric-geometry",
};

function canonicalView(view: V5View): V5View {
  return VIEW_ALIAS[view] ?? view;
}

export interface ThreadV5StartMenuProps {
  activeView: V5View;
  activeDashboardName?: string | null;
  dashboards: ThreadV4ViewSpec[];
  readOnly?: boolean;
  chatOpen?: boolean;
  onOpenView: (view: V5View) => void;
  onOpenDashboard: (view: ThreadV4ViewSpec) => void;
  onOpenChat?: () => void;
  onSignIn?: () => void;
}

export function ThreadV5StartMenu({
  activeView,
  activeDashboardName,
  dashboards,
  readOnly = false,
  chatOpen = false,
  onOpenView,
  onOpenDashboard,
  onOpenChat,
  onSignIn,
}: ThreadV5StartMenuProps) {
  const triggerId = useId();
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const focusLastOnOpenRef = useRef(false);
  const [open, setOpen] = useState(false);
  const visibleDashboards = dashboards;
  const activeDashboardKey = activeDashboardName ?? "";

  const menuItems = useCallback(() => (
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>(
      '[role="menuitem"]:not(:disabled):not([aria-disabled="true"])',
    ) ?? [])
  ), []);

  const closeAndRestoreFocus = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const items = menuItems();
    const target = focusLastOnOpenRef.current ? items[items.length - 1] : items[0];
    focusLastOnOpenRef.current = false;
    target?.focus();
  }, [menuItems, open]);

  useEffect(() => {
    if (!open) return;
    const closeFromOutside = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closeAndRestoreFocus();
    };
    document.addEventListener("click", closeFromOutside);
    return () => document.removeEventListener("click", closeFromOutside);
  }, [closeAndRestoreFocus, open]);

  const openFromKeyboard = (last: boolean) => {
    focusLastOnOpenRef.current = last;
    setOpen(true);
  };

  const activate = (action: () => void) => {
    closeAndRestoreFocus();
    action();
  };

  const handleMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      closeAndRestoreFocus();
      return;
    }
    if (event.key === "Tab") {
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    const items = menuItems();
    if (!items.length) return;
    const currentIndex = Math.max(0, items.indexOf(document.activeElement as HTMLButtonElement));
    let nextIndex: number | null = null;
    if (event.key === "ArrowDown") nextIndex = (currentIndex + 1) % items.length;
    if (event.key === "ArrowUp") nextIndex = (currentIndex - 1 + items.length) % items.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = items.length - 1;
    if (nextIndex == null) return;
    event.preventDefault();
    items[nextIndex]?.focus();
  };

  const destinationItem = (item: MenuDestination) => {
    const isActive = canonicalView(activeView) === item.view && !activeDashboardName;
    return (
      <button
        key={item.view}
        className={`v5-start-menu-item${isActive ? " is-active" : ""}`}
        type="button"
        role="menuitem"
        tabIndex={-1}
        aria-current={isActive ? "page" : undefined}
        aria-label={`Open ${item.label}`}
        onClick={() => activate(() => onOpenView(item.view))}
      >
        {item.icon}
        <span>{item.label}</span>
      </button>
    );
  };

  return (
    <div className="v5-start-menu-root" ref={rootRef} data-testid="v5-start-menu-root">
      <button
        ref={triggerRef}
        id={triggerId}
        className={`v5-start-menu-trigger${open ? " is-open" : ""}`}
        type="button"
        aria-label="Thread menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        title="Thread menu"
        data-testid="v5-start-menu-trigger"
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openFromKeyboard(event.key === "ArrowUp");
          }
        }}
      >
        <ThreadLogo height={24} showWordmark={false} />
        <Icons.ChevronDown size={12} />
      </button>

      {open ? (
        <div
          ref={menuRef}
          id={menuId}
          className="v5-start-menu-surface"
          role="menu"
          aria-labelledby={triggerId}
          data-testid="v5-start-menu"
          onKeyDown={handleMenuKeyDown}
        >
          <header className="v5-start-menu-header">
            <span>Thread</span>
            <small>Destinations and layouts</small>
          </header>
          <div className="v5-start-menu-body">
            <div className="v5-start-menu-destinations">
              {MENU_GROUPS.map((group) => {
                const items = group.items.filter((item) => !(readOnly && item.authOnly));
                if (!items.length) return null;
                const groupLabelId = `${menuId}-${group.id}`;
                return (
                  <section className="v5-start-menu-group" role="group" aria-labelledby={groupLabelId} key={group.id}>
                    <h2 id={groupLabelId}>{group.label}</h2>
                    {items.map(destinationItem)}
                  </section>
                );
              })}
            </div>

            <aside className="v5-start-menu-layouts" role="group" aria-labelledby={`${menuId}-layouts`}>
              <h2 id={`${menuId}-layouts`}>Layouts</h2>
              {visibleDashboards.length ? visibleDashboards.map((dashboard) => {
                const isActive = activeView === "workbench" && [dashboard.id, dashboard.slug, dashboard.name].includes(activeDashboardKey);
                return (
                  <button
                    key={dashboard.id}
                    className={`v5-start-menu-item${isActive ? " is-active" : ""}`}
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    aria-current={isActive ? "page" : undefined}
                    aria-label={`Open layout ${dashboard.name}`}
                    onClick={() => activate(() => onOpenDashboard(dashboard))}
                  >
                    <Icons.Stack size={15} />
                    <span>{dashboard.name}</span>
                  </button>
                );
              }) : (
                <span className="v5-start-menu-empty">{readOnly ? "No shared layouts" : "No saved layouts"}</span>
              )}
              {!readOnly ? (
                <button className="v5-start-menu-item" type="button" role="menuitem" tabIndex={-1} aria-label="New layout" disabled>
                  <Icons.Plus size={15} />
                  <span>New layout</span>
                  <em>Soon</em>
                </button>
              ) : null}

              <h2 className="v5-start-menu-utility-heading">Utilities</h2>
              <button
                className="v5-start-menu-item"
                type="button"
                role="menuitem"
                tabIndex={-1}
                aria-label="Report issue"
                onClick={() => activate(openThreadFeedbackReporter)}
              >
                <Icons.Info size={15} />
                <span>Report issue</span>
              </button>
              {onOpenChat ? (
                <button
                  className={`v5-start-menu-item${chatOpen ? " is-active" : ""}`}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  aria-label="Open Ask Thread"
                  onClick={() => activate(onOpenChat)}
                >
                  <Icons.Comments size={15} />
                  <span>Ask Thread</span>
                </button>
              ) : null}
              {readOnly && onSignIn ? (
                <>
                  <h2 className="v5-start-menu-utility-heading">Account</h2>
                  <button
                    className="v5-start-menu-item"
                    type="button"
                    role="menuitem"
                    tabIndex={-1}
                    onClick={() => activate(onSignIn)}
                  >
                    <Icons.User size={15} />
                    <span>Sign in to Thread</span>
                  </button>
                </>
              ) : null}
            </aside>
          </div>
        </div>
      ) : null}
    </div>
  );
}
